import { formatDistanceToNow } from "date-fns"

import { EntityDetail, EntityDetailColumn, EntityDetailRow } from "@/components/EntityDetail"
import Icon from "@/components/Icon"
import ShiftDetailsDialog from "@/components/sheriff/shift/ShiftDetailsDialog"
import { BlockSection } from "@/components/sheriff/ui/BlockSection"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  BatteryCell,
  ChargingCell,
  DrivingCell,
  ExternalLinksCell,
  LockStateCell,
  StateAgeCell,
} from "@/components/vehicles/VehicleStateCells"
import { FAILURE_REASONS } from "@/constants/vehicles"
import { useShiftDialog } from "@/hooks/useShiftDialog"
import { useUnassignVehicle } from "@/hooks/useVehicleAssignment"
import {
  type Vehicle,
  type VehicleAuditEntry,
  type VehicleListRow,
  useVehicleAuditLog,
  useVehicleShifts,
} from "@/hooks/useVehicles"

const EVENT_LABELS: Record<string, string> = {
  lock: "Lock",
  unlock: "Unlock",
  state_observed: "State observed",
}

const SOURCE_LABELS: Record<string, string> = {
  pin_portal: "PIN portal",
  team_app: "Team app",
  resourcing_hub_admin: "Admin portal",
  system: "System",
}

function mapsHref(latitude: number, longitude: number) {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
}

function Blank() {
  return <span className="text-muted-foreground italic">-</span>
}

const getBadgeClassesByStatus = (status: string | null) => {
  switch (status) {
    case "Active":
    case "Available":
      return "bg-delivered text-indicators-green border-transparent"
    case "Insurance Write Off":
    case "Defleeting":
    case "Returned":
      return "bg-palette-angry100 text-indicators-red border-transparent"
    case "Insurance Repair":
    case "Repair Off-site (bodyshop)":
    case "Repair Off-site (mechanical)":
    case "Repair On-site":
      return "bg-palette-honeycomb100 text-palette-honeycomb800 border-transparent"
    default:
      return "bg-palette-jungleGreen000 text-palette-jungleGreen600 border-transparent"
  }
}

function VehicleSummary({ row }: { row: VehicleListRow }) {
  const { vehicle, depotName, carParkName, inversReference, zerityReference } = row

  return (
    <>
      <EntityDetailColumn>
        <EntityDetailRow icon="depot" label="Depot">
          {depotName ?? "N/A"}
        </EntityDetailRow>
        <EntityDetailRow icon="mapPinLine" label="Car park">
          {carParkName ?? "N/A"}
        </EntityDetailRow>
        <EntityDetailRow icon="company" label="Group">
          {vehicle.vehicle_group ?? "N/A"}
        </EntityDetailRow>
      </EntityDetailColumn>

      <EntityDetailColumn>
        <EntityDetailRow icon="lock" label="Lock state">
          <LockStateCell lockState={vehicle.lock_state} />
        </EntityDetailRow>
        <EntityDetailRow icon="truck" label="Driving">
          <DrivingCell lockState={vehicle.lock_state} drivingEnabled={vehicle.driving_enabled} />
        </EntityDetailRow>
      </EntityDetailColumn>

      <EntityDetailColumn>
        <EntityDetailRow icon="lightning" label="Battery">
          <BatteryCell
            batteryPercent={vehicle.battery_percent}
            chargingStatus={vehicle.charging_status}
            showChargingCaption={false}
          />
        </EntityDetailRow>
        <EntityDetailRow icon="batteryChargingVertical" label="Charging">
          <ChargingCell chargingStatus={vehicle.charging_status} />
        </EntityDetailRow>
        <EntityDetailRow icon="clock" label="State age">
          <StateAgeCell observedAt={vehicle.lock_state_observed_at} />
        </EntityDetailRow>
        <EntityDetailRow icon="arrowUpRight" label="Links">
          <ExternalLinksCell zerityReference={zerityReference} inversReference={inversReference} />
        </EntityDetailRow>
      </EntityDetailColumn>
    </>
  )
}

function PositionCard({ vehicle }: { vehicle: VehicleListRow["vehicle"] }) {
  const hasPosition = vehicle.latitude !== null && vehicle.longitude !== null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Position</CardTitle>
      </CardHeader>
      <CardContent>
        {hasPosition ? (
          <div className="flex flex-col gap-2">
            <span className="font-mono text-sm">
              {vehicle.latitude}, {vehicle.longitude}
            </span>
            {/* The position rides along with the lock state, so it is only as fresh as that read. */}
            <span className="text-muted-foreground text-xs">
              As at the last lock state reading,{" "}
              <StateAgeCell observedAt={vehicle.lock_state_observed_at} />
            </span>
            <a
              href={mapsHref(vehicle.latitude, vehicle.longitude)}
              target="_blank"
              rel="noreferrer"
              className="flex w-fit items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Open in Google Maps
              <Icon name="arrowUpRight" className="h-3 w-3 text-current" />
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm italic">No position recorded</span>
        )}
      </CardContent>
    </Card>
  )
}

function ShiftsCard({ vehicle }: { vehicle: Vehicle }) {
  const vehicleId = vehicle.id
  const unassign = useUnassignVehicle()
  const { data, isLoading, error } = useVehicleShifts(vehicleId)
  const { shiftDetailsOpen, setShiftDetailsOpen, selectedShift, handleShiftClick } =
    useShiftDialog()

  const upcoming = data?.upcoming ?? []
  const past = data?.past ?? []
  const allShifts = [...upcoming, ...past]

  // selectedShift is a snapshot taken on click. The list is filtered to this vehicle, so a block
  // that has dropped out of it no longer holds one.
  const openShift = selectedShift
    ? (() => {
        const fresh = allShifts.find((s) => s.id === selectedShift.id)
        return {
          ...selectedShift,
          vehicleId: fresh?.vehicle?.id ?? null,
          vehicleReg: fresh?.vehicle?.reg ?? null,
        }
      })()
    : null

  return (
    <Card>
      <CardContent className="pt-6">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner size="md" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <Icon name="warningCircleFilled" className="h-4 w-4" />
            <AlertDescription>
              Error loading blocks: {error instanceof Error ? error.message : "Unknown error"}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="flex flex-col gap-6">
            <BlockSection
              tone="upcoming"
              shifts={upcoming}
              showCount={false}
              showPerson
              canEdit={false}
              onEdit={handleShiftClick}
              onView={handleShiftClick}
            />
            <BlockSection
              tone="past"
              shifts={past}
              showCount={false}
              showPerson
              canEdit={false}
              onView={handleShiftClick}
            />
          </div>
        )}
      </CardContent>
      <ShiftDetailsDialog
        mode="view"
        shift={openShift}
        open={shiftDetailsOpen}
        onOpenChange={setShiftDetailsOpen}
        isUnassigningVehicle={unassign.isPending}
        onUnassignVehicle={(shiftId) => unassign.mutate({ shiftId, vehicleId })}
      />
    </Card>
  )
}

function AuditResultCell({ entry }: { entry: VehicleAuditEntry }) {
  if (!entry.result) return <Blank />

  if (entry.result === "success") {
    return (
      <Badge variant="secondary" className="w-fit">
        Success
      </Badge>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <Badge variant="destructive" className="w-fit">
        {entry.result}
      </Badge>
      {entry.failure_reason && (
        <span className="text-xs text-amber-700">
          {FAILURE_REASONS[entry.failure_reason] ?? entry.failure_reason}
        </span>
      )}
    </div>
  )
}

function AuditLogCard({ vehicleId }: { vehicleId: string }) {
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useVehicleAuditLog(vehicleId)

  const entries = (data?.pages ?? []).flat()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Audit log</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner size="md" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <Icon name="warningCircleFilled" className="h-4 w-4" />
            <AlertDescription>
              Error loading audit log: {error instanceof Error ? error.message : "Unknown error"}
            </AlertDescription>
          </Alert>
        ) : entries.length === 0 ? (
          <span className="text-muted-foreground text-sm italic">
            No recorded activity for this vehicle
          </span>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Who</TableHead>
                  <TableHead>What</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Where the van was</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={`${entry.id}-${entry.created_at}`}>
                    <TableCell>
                      <span title={new Date(entry.created_at).toLocaleString()}>
                        {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span>
                          {entry.actorName ??
                            (entry.source === "system" ? "System" : "Unknown actor")}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {SOURCE_LABELS[entry.source] ?? entry.source}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{EVENT_LABELS[entry.event_type] ?? entry.event_type}</TableCell>
                    <TableCell>
                      <AuditResultCell entry={entry} />
                    </TableCell>
                    <TableCell>
                      {/* Only a successful command carries observed state, so most failures are blank. */}
                      {entry.observed_latitude !== null && entry.observed_longitude !== null ? (
                        <a
                          href={mapsHref(entry.observed_latitude, entry.observed_longitude)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Map
                          <Icon name="arrowUpRight" className="h-3 w-3 text-current" />
                        </a>
                      ) : (
                        <Blank />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {hasNextPage && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                {isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface VehicleDetailProps {
  row: VehicleListRow
  onClose: () => void
}

export default function VehicleDetail({ row, onClose }: VehicleDetailProps) {
  const { vehicle, inversReference, zerityReference } = row

  return (
    <EntityDetail
      icon="truck"
      title={vehicle.registration_display}
      titleAdornments={
        <>
          <Badge
            className={`pointer-events-none shrink-0 ${getBadgeClassesByStatus(vehicle.operational_status)}`}
          >
            {vehicle.operational_status ?? "Unknown status"}
          </Badge>
          {/* In INVERS but not Zerity: unlockable, yet invisible to ops elsewhere. */}
          {inversReference && !zerityReference && (
            <span className="flex items-center gap-1 text-xs text-palette-honeycomb800">
              <Icon name="warning" className="h-3 w-3 text-current" />
              Not in Zerity
            </span>
          )}
        </>
      }
      summary={<VehicleSummary row={row} />}
      onClose={onClose}
    >
      <Tabs defaultValue="shifts" className="h-full">
        <TabsList className="flex h-auto w-full justify-start overflow-x-auto overflow-y-hidden bg-palette-jungleGreen000">
          <TabsTrigger className="shrink-0" value="shifts">
            Blocks
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="audit">
            Audit log
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="position">
            Position
          </TabsTrigger>
        </TabsList>
        <div className="mt-6 h-full">
          <TabsContent value="shifts" className="h-full">
            <ShiftsCard vehicle={vehicle} />
          </TabsContent>
          <TabsContent value="audit" className="h-full">
            <AuditLogCard vehicleId={vehicle.id} />
          </TabsContent>
          <TabsContent value="position" className="h-full">
            <PositionCard vehicle={vehicle} />
          </TabsContent>
        </div>
      </Tabs>
    </EntityDetail>
  )
}
